import React, { useEffect, useState, useRef } from 'react';
import { HealthBuddy, BuddyMood } from './HealthBuddy';
import { ResultCard } from './ResultCard';
import { MicIcon, ChevronDownIcon } from './icons';
import { ModalKind, RESULT_CARDS, SCRIPTS, Script } from '../data/mock';
type Msg = {
  id: number;
  role: 'user' | 'assistant';
  text?: string;
  card?: string;
  thinking?: boolean;
};
let msgCounter = 0;
const nextId = () => ++msgCounter;
/**
 * The expanded conversation — voice + chat hybrid (blueprint §8).
 * Every voice interaction appears as text. Every successful query
 * resolves into a result card. The assistant finds curated content;
 * it never generates advice.
 */
export function ChatSheet({
  open,
  onClose,
  onOpenModal,
  pendingScript,
  onScriptConsumed
}: {
  open: boolean;
  onClose: () => void;
  onOpenModal: (modal: ModalKind, start?: string) => void;
  pendingScript: string | null;
  onScriptConsumed: () => void;
}) {
  const [messages, setMessages] = useState<Msg[]>([{
    id: nextId(),
    role: 'assistant',
    text: "Morning, Maya ☀️ I'm here whenever you need me — ask me anything, or try one of the suggestions below."
  }]);
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [usedScripts, setUsedScripts] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const runScript = (script: Script) => {
    setBusy(true);
    setUsedScripts(u => [...u, script.id]);
    setMessages(m => [...m, {
      id: nextId(),
      role: 'user',
      text: script.spoken
    }]);
    /* priority scripts (emergency) never make the user wait — no thinking
       bubble, near-immediate reply */
    const replyDelay = script.priority ? 350 : 1700;
    if (!script.priority) {
      setTimeout(() => {
        setMessages(m => [...m, {
          id: nextId(),
          role: 'assistant',
          thinking: true
        }]);
      }, 400);
    }
    setTimeout(() => {
      setMessages(m => [...m.filter(x => !x.thinking), {
        id: nextId(),
        role: 'assistant',
        text: script.reply,
        card: script.card
      }]);
      setBusy(false);
      /* auto-open routing — the emergency steps open by themselves; the
         result card stays in the thread as a way back in later */
      if (script.autoOpen) {
        const card = RESULT_CARDS[script.card];
        setTimeout(() => onOpenModal(card.modal, card.start), 900);
      }
    }, replyDelay);
  };
  const runScriptById = (id: string) => {
    const script = SCRIPTS.find(s => s.id === id);
    if (script && !busy) runScript(script);
  };
  // a suggestion chip tapped on the dock opens the sheet with a pending script
  useEffect(() => {
    if (open && pendingScript) {
      const t = setTimeout(() => {
        runScriptById(pendingScript);
        onScriptConsumed();
      }, 450);
      return () => clearTimeout(t);
    }
  }, [open, pendingScript]);
  // voice input demo — listening state, then the query appears as text
  const handleMic = () => {
    if (busy || listening) return;
    setListening(true);
    const next = SCRIPTS.find(s => !usedScripts.includes(s.id)) ?? SCRIPTS[0];
    setTimeout(() => {
      setListening(false);
      runScript(next);
    }, 2200);
  };
  // keep the latest message in view
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, listening]);
  const buddyMood: BuddyMood = listening ? 'listening' : busy ? 'thinking' : 'idle';
  return <div className={`absolute inset-0 z-30 flex flex-col bg-linen transition-transform duration-500 ease-[cubic-bezier(0.22,0.8,0.36,1)] ${open ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-66">
      {/* header */}
      <div className="flex shrink-0 items-center justify-between border-b border-linen-deep bg-cream px-4 pb-3 pt-12" data-id="element-67">
        <div className="flex items-center gap-3" data-id="element-68">
          <HealthBuddy size={44} mood={buddyMood} data-id="element-69" />
          <div data-id="element-70">
            <h2 className="font-display text-[17px] font-semibold leading-tight text-ink" data-id="element-71">
              Sunny
            </h2>
            <p className="text-[11.5px] font-semibold text-teal" data-id="element-72">
              {listening ? 'Listening…' : busy ? 'Finding the right thing…' : 'Here to help'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-linen text-ink-soft transition-colors hover:bg-linen-deep" data-id="element-73">
          <ChevronDownIcon size={18} data-id="element-74" />
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-id="element-75">
        <div className="flex flex-col gap-3.5" data-id="element-76">
          {messages.map(msg => msg.role === 'user' ? <div key={msg.id} className="dhp-fade-up flex justify-end" data-id="element-77">
                <div className="max-w-[80%] rounded-bubble rounded-br-md bg-teal px-4 py-2.5 text-[14px] font-medium leading-snug text-cream" data-id="element-78">
                  {msg.text}
                </div>
              </div> : <div key={msg.id} className="dhp-fade-up flex items-end gap-2" data-id="element-79">
                <div className="mb-1 shrink-0" data-id="element-80">
                  <HealthBuddy size={26} mood={msg.thinking ? 'thinking' : 'idle'} data-id="element-81" />
                </div>
                <div className="max-w-[82%]" data-id="element-82">
                  {msg.thinking ? <div className="flex items-center gap-1.5 rounded-bubble rounded-bl-md bg-cream px-4 py-3.5 shadow-card" data-id="element-83">
                      <span className="dhp-thinking-dot h-2 w-2 rounded-full bg-teal" data-id="element-84" />
                      <span className="dhp-thinking-dot h-2 w-2 rounded-full bg-teal" style={{
                animationDelay: '0.15s'
              }} data-id="element-85" />
                      <span className="dhp-thinking-dot h-2 w-2 rounded-full bg-teal" style={{
                animationDelay: '0.3s'
              }} data-id="element-86" />
                    </div> : <>
                      <div className="rounded-bubble rounded-bl-md bg-cream px-4 py-2.5 text-[14px] leading-snug text-ink shadow-card" data-id="element-87">
                        {msg.text}
                      </div>
                      {msg.card && <div className="mt-2" data-id="element-88">
                          <ResultCard data={RESULT_CARDS[msg.card]} onOpen={() => onOpenModal(RESULT_CARDS[msg.card].modal, RESULT_CARDS[msg.card].start)} data-id="element-89" />
                        </div>}
                    </>}
                </div>
              </div>)}

          {/* listening indicator — the spoken words will appear as text */}
          {listening && <div className="dhp-fade-up flex justify-end" data-id="element-90">
              <div className="flex items-center gap-2.5 rounded-bubble rounded-br-md border border-[#17695E2b] bg-teal-soft px-4 py-3" data-id="element-91">
                <div className="flex h-5 items-center gap-[3px]" data-id="element-92">
                  {[0, 1, 2, 3, 4].map(i => <span key={i} className="dhp-wave-bar w-[3px] rounded-full bg-teal" style={{
                height: '100%',
                animationDelay: `${i * 0.12}s`
              }} data-id="element-93" />)}
                </div>
                <span className="text-[13px] font-bold text-teal-deep" data-id="element-94">
                  Listening…
                </span>
              </div>
            </div>}
        </div>
      </div>

      {/* canned suggestions — pale, sitting above the divider line inside the
              messages area; one row that scrolls sideways, chip text wraps to
              two lines */}
      <div className="shrink-0 bg-linen px-4 pb-2 pt-1" data-id="element-95">
        <div className="no-scrollbar flex w-full gap-2 overflow-x-auto overscroll-x-contain" data-id="element-96">
          {SCRIPTS.filter(s => !usedScripts.includes(s.id)).map(s => <button key={s.id} onClick={() => runScriptById(s.id)} className="line-clamp-2 w-[150px] shrink-0 rounded-2xl border border-linen-deep bg-cream px-3.5 py-2 text-left text-[12px] font-semibold leading-snug text-ink-soft shadow-card transition-all hover:-translate-y-0.5 hover:text-teal-deep active:scale-95" data-id="element-97">
              {s.chip}
            </button>)}
        </div>
      </div>

      {/* input row */}
      <div className="shrink-0 border-t border-linen-deep bg-cream px-4 pb-8 pt-3" data-id="element-98">
        {/* the chat box — buddy + placeholder + voice */}
        <div className="flex items-center gap-2.5 rounded-full border border-linen-deep bg-cream p-2 pl-3 shadow-dock" data-id="element-99">
          <HealthBuddy size={38} mood={buddyMood} data-id="element-100" />
          <span className="flex-1 text-[14px] font-medium text-ink-faint" data-id="element-101">
            Ask Sunny anything…
          </span>
          <button onClick={handleMic} className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-cream shadow-card transition-all active:scale-95 ${listening ? 'bg-coral' : 'bg-teal hover:scale-105'}`} data-id="element-102">
            {listening && <span className="dhp-ring absolute inset-0 rounded-full bg-coral" data-id="element-103" />}
            <MicIcon size={19} className="relative" data-id="element-104" />
          </button>
        </div>
      </div>
    </div>;
}